/* eslint-disable @typescript-eslint/no-explicit-any */
import { AuthBindings, Authenticated, Refine } from '@refinedev/core'
import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar'
import {
  notificationProvider,
  RefineThemes,
  ThemedLayoutV2,
} from '@refinedev/mantine'
import routerProvider, {
  DocumentTitleHandler,
  UnsavedChangesNotifier,
} from '@refinedev/nextjs-router'
import type { NextPage } from 'next'
import { AppProps } from 'next/app'
import { Header } from '@components/common/header'
import {
  Card,
  ColorScheme,
  ColorSchemeProvider,
  Global,
  LoadingOverlay,
  MantineProvider,
} from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import { NotificationsProvider } from '@mantine/notifications'
import dataProvider from '@refinedev/simple-rest'
import { appWithTranslation, useTranslation } from 'next-i18next'
import ILogin from 'src/interfaces/login'
import {
  clearDados,
  loginAuth,
  verifyUserExpired,
} from '../src/utils/Authentication'
import { useRouter } from 'next/router'
import { useLoadingStore } from 'src/stores/LoadingStore'
import Cookies from 'js-cookie'
import 'dayjs/locale/pt-br'
import { SuccessNotification } from '@components/common/notification'
import { FooterSimple } from '@components/common/footer'
import { Menu } from '@components/common/side'
import {
  IconUsers,
  IconUserShield,
  IconBuildingFactory2,
  IconPackage,
  IconStack2,
  IconMoneybagMoveBack,
  IconMoneybagMove,
  IconLayoutDashboard,
  IconShoppingBag,
  IconCashRegister,
  IconCalendarEvent,
  IconFileInvoice,
  IconCash,
  IconBook,
} from '@tabler/icons-react'
import { accessControl } from 'src/utils/ValidatePermissionsUser'

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  noLayout?: boolean
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}
function MyApp({ Component, pageProps }: AppPropsWithLayout): JSX.Element {
  const isLoading = useLoadingStore(state => state.isLoading)
  const authProvider: AuthBindings = {
    login: async ({ username, password }) => {
      clearDados()
      const user: ILogin = {
        login: username,
        senha: password,
      }
      const confirm = await loginAuth(user)
      return {
        authenticated: confirm ? true : false,
        redirectTo: 'home',
        success: confirm,
      }
    },
    check: async () => {
      try {
        const check: boolean = Cookies.get('nome')
          ? await verifyUserExpired()
          : false
        if (check) {
          return {
            authenticated: true,
          }
        } else {
          return {
            authenticated: false,
            redirectTo: '/login',
          }
        }
      } catch (error) {
        return {
          authenticated: false,
          redirectTo: '/login',
        }
      }
    },
    getIdentity: async () => {
      return {
        role: Cookies.get('role'),
        nome: Cookies.get('nome'),
        pathImage: Cookies.get('pathImage'),
        login: Cookies.get('dados_usuario'),
        permissions: Cookies.get('permissions'),
        menuCollapse: localStorage.getItem('menu-collapsed'),
      }
    },
    logout: async () => {
      clearDados()
      SuccessNotification({ message: 'Desconectado com sucesso!' })
      return {
        success: true,
        authenticated: false,
        redirectTo: '/login',
      }
    },
    onError: async () => ({}),
  }
  const router = useRouter()
  const renderComponent = () => {
    if (
      router.pathname === '/login' ||
      router.asPath.startsWith('/catalogo-publico')
    ) {
      return <Component {...pageProps} />
    }

    return (
      <ThemedLayoutV2
        initialSiderCollapsed={false}
        Header={() => <></>}
        Sider={() => <Menu />}
        Footer={() => <FooterSimple />}
      >
        <Authenticated
          appendCurrentPathToQuery={false}
          loading
          key={'authenticated'}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '86.5vh',
            }}
          >
            <Header />
            <Card shadow="sm" radius="md" withBorder>
              <Component {...pageProps} />
            </Card>
          </div>
        </Authenticated>
      </ThemedLayoutV2>
    )
  }

  const [colorScheme, setColorScheme] = useLocalStorage<ColorScheme>({
    key: 'mantine-color-scheme',
    defaultValue: 'light',
    getInitialValueInEffect: true,
  })
  const { t, i18n } = useTranslation()
  const toggleColorScheme = (value?: ColorScheme) => {
    setColorScheme(value || (colorScheme === 'light' ? 'dark' : 'light'))
  }

  const i18nProvider = {
    translate: (key: string, params: object) => t(key, params),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  }

  return (
    <>
      <RefineKbarProvider>
        <ColorSchemeProvider
          colorScheme={colorScheme}
          toggleColorScheme={toggleColorScheme}
        >
          <MantineProvider
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            theme={{
              ...RefineThemes.Blue,
              colors: {
                brand: [
                  '#e6f2fc',
                  '#b3d9f7',
                  '#80c0f2',
                  '#4da7ed',
                  '#1f7cd0',
                  '#1b6dbb',
                  '#228be6',
                  '#165da5',
                  '#104d8f',
                  '#0b3d79',
                ],
                blue: [
                  '#e6f2fc',
                  '#b3d9f7',
                  '#80c0f2',
                  '#4da7ed',
                  '#1f7cd0',
                  '#228be6',
                  '#1b6dbb',
                  '#165da5',
                  '#104d8f',
                  '#0b3d79',
                ],
              },
              colorScheme: colorScheme,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontFamilyMonospace: 'Monaco, Courier, monospace',
              headings: { fontFamily: "'Plus Jakarta Sans', sans-serif" },
              components: {
                InputLabel: {
                  defaultProps: {
                    fw: 'bold',
                  },
                },
                Popover: {
                  defaultProps: {
                    zIndex: 10000000,
                    withinPortal: true,
                    fw: 'bold',
                  },
                },
                DatesProvider: {
                  defaultProps: {
                    settings: { locale: 'pt-br' },
                    withinPortal: true,
                  },
                },
                Drawer: {
                  defaultProps: {
                    zIndex: 100000,
                    withinPortal: true,
                    fw: 'bold',
                  },
                },
                ScrollArea: {
                  defaultProps: {
                    type: 'never',
                  },
                },
                Tooltip: {
                  defaultProps: {
                    zIndex: 1000000,
                    fw: 'bold',
                  },
                },
                Badge: {
                  defaultProps: {
                    size: 'lg',
                  },
                },
                Select: {
                  defaultProps: {
                    zIndex: 10000000,
                    fw: 'bold',
                  },
                },
                NumberInput: {
                  defaultProps: {
                    decimalSeparator: '.',
                  },
                },
                Modal: {
                  defaultProps: {
                    withCloseButton: false,
                    closeOnEscape: false,
                    trapFocus: false,
                    radius: 10,
                    overlayProps: { blur: 10 },
                    withinPortal: true,
                    zIndex: 1000000,
                    centered: true,
                  },
                },
                Card: {
                  defaultProps: {
                    radius: 10,
                  },
                },
                Button: {
                  defaultProps: {
                    size: 'sm',
                    radius: 5,
                    variant: 'filled',
                    fw: 'bold',
                  },
                  styles: {
                    root: {
                      transition: 'all 150ms ease',
                      '&:active': {
                        transform: 'translateY(0)',
                        boxShadow: 'none',
                      },
                    },
                  },
                },
                Input: {
                  defaultProps: {
                    fw: 'bold',
                  },
                },
                Title: {
                  defaultProps: {
                    fw: 'bold',
                  },
                },
              },
            }}
            withNormalizeCSS
            withGlobalStyles
          >
            <LoadingOverlay
              visible={isLoading}
              loaderProps={{ size: 'md', variant: 'oval' }}
              overlayBlur={2}
              overlayOpacity={0.6}
              zIndex={10000000}
            />
            <Global styles={{ body: { WebkitFontSmoothing: 'auto' } }} />
            <NotificationsProvider position="top-right" zIndex={10000000}>
              <Refine
                authProvider={authProvider}
                routerProvider={routerProvider}
                accessControlProvider={{
                  can: async ({ resource }) => {
                    try {
                      return accessControl(resource)
                    } catch (error) {
                      console.error('Erro ao verificar permissões:', error)
                      return { can: false }
                    }
                  },
                }}
                dataProvider={dataProvider('/api')}
                notificationProvider={notificationProvider}
                i18nProvider={i18nProvider}
                resources={[
                  {
                    name: 'dashboard',
                    list: '/dashboard',
                    meta: {
                      canDelete: false,
                      label: 'DashBoard',
                      icon: <IconLayoutDashboard />,
                    },
                  },
                  {
                    name: 'catalogo',
                    list: '/catalogo/list',
                    show: '/catalogo/show/:id',
                    create: '/catalogo/create',
                    edit: '/catalogo/edit/:id',
                    meta: {
                      canDelete: false,
                      label: 'Catálogo',
                      icon: <IconBook />,
                    },
                  },
                  {
                    name: 'eventos',
                    list: '/eventos',
                    meta: {
                      canDelete: false,
                      label: 'Eventos',
                      icon: <IconCalendarEvent />,
                    },
                  },
                  {
                    name: 'produtos',
                    show: '/produtos/show/:id',
                    list: '/produtos/list',
                    create: '/produtos/create',
                    edit: '/produtos/edit/:id',
                    meta: {
                      canDelete: false,
                      label: 'Produtos',
                      icon: <IconPackage />,
                    },
                  },
                  {
                    name: 'grupolinha',
                    show: '/grupolinha/show/:id',
                    list: '/grupolinha/list',
                    create: '/grupolinha/create',
                    edit: '/grupolinha/edit/:id',
                    meta: {
                      canDelete: false,
                      label: 'Grupo/Linha',
                      icon: <IconStack2 />,
                    },
                  },
                  {
                    name: 'fornecedores',
                    show: '/fornecedores/show/:id',
                    list: '/fornecedores/list',
                    create: '/fornecedores/create',
                    edit: '/fornecedores/edit/:id',
                    meta: {
                      label: 'Fornecedores',
                      icon: <IconBuildingFactory2 />,
                    },
                  },
                  {
                    name: 'clientes',
                    show: '/clientes/show/:id',
                    list: '/clientes/list',
                    create: '/clientes/create',
                    edit: '/clientes/edit/:id',
                    meta: {
                      label: 'Clientes',
                      icon: <IconUsers />,
                    },
                  },
                  {
                    name: 'compras',
                    show: '/compras/show/:id',
                    list: '/compras/list',
                    create: '/compras/create',
                    edit: '/compras/edit/:id',
                    meta: {
                      label: 'Compras',
                      icon: <IconShoppingBag />,
                    },
                  },
                  {
                    name: 'orcamentos',
                    show: '/orcamentos/show/:id',
                    list: '/orcamentos/list',
                    create: '/orcamentos/create',
                    edit: '/orcamentos/edit/:id',
                    meta: {
                      canDelete: false,
                      label: 'Orçamentos',
                      icon: <IconFileInvoice />,
                    },
                  },
                  {
                    name: 'vendas',
                    show: '/vendas/show/:id',
                    list: '/vendas/list',
                    create: '/vendas/create',
                    edit: '/vendas/edit/:id',
                    meta: {
                      label: 'Vendas',
                      icon: <IconCashRegister />,
                    },
                  },
                  {
                    name: 'caixa',
                    show: '/caixa/show/:id',
                    list: '/caixa/list',
                    create: '/caixa/create',
                    edit: '/caixa/edit/:id',
                    meta: {
                      label: 'Caixa',
                      icon: <IconCash />,
                    },
                  },
                  {
                    name: 'contaspagar',
                    show: '/contaspagar/show/:id',
                    list: '/contaspagar/list',
                    create: '/contaspagar/create',
                    edit: '/contaspagar/edit/:id',
                    meta: {
                      label: 'Contas a pagar',
                      icon: <IconMoneybagMove />,
                    },
                  },
                  {
                    name: 'contasreceber',
                    show: '/contasreceber/show/:id',
                    list: '/contasreceber/list',
                    create: '/contasreceber/create',
                    edit: '/contasreceber/edit/:id',
                    meta: {
                      label: 'Contas a receber',
                      icon: <IconMoneybagMoveBack />,
                    },
                  },
                  {
                    name: 'usuario',
                    list: '/usuario/list',
                    meta: {
                      label: 'Usuários',
                      icon: <IconUserShield />,
                    },
                  },
                ]}
                options={{
                  syncWithLocation: true,
                  warnWhenUnsavedChanges: true,
                }}
              >
                {renderComponent()}
                <RefineKbar />
                <UnsavedChangesNotifier />
                <DocumentTitleHandler handler={() => 'DM - Pai e filhos '} />
              </Refine>
            </NotificationsProvider>
          </MantineProvider>
        </ColorSchemeProvider>
      </RefineKbarProvider>
    </>
  )
}

export default appWithTranslation(MyApp)
